import type { FC, ReactNode } from "react";
import { useSlotClaimedElsewhere } from "./useTemplate";
import { useTemplatedScope } from "./TemplatedScope";
import { hasShellTemplate } from "./discoverTemplates";
// `Templated` is imported here as a render-time value (inside the Slot
// component's return). The corresponding `import { Slot } from "./Slot"`
// inside Templated.tsx is also render-time (used by harvestSlots for a
// `node.type === Slot` comparison). ES modules tolerate this kind of
// cycle because neither side touches the imported binding at module
// init — both reads happen during render.
import { Templated } from "./Templated";

export interface SlotProps {
  name: string;
  children?: ReactNode;
  /**
   * A hidden slot is registered (so a container template cannot fall back to
   * its own content for it) but renders nothing at all: no children, no
   * `data-slot-name` marker, no per-slot `<template id="lastbot-X">` override.
   * Used when a feature is switched off for the account (LAS-4711,
   * `composer-attach`).
   */
  hidden?: boolean;
}

/**
 * Public marker used inside default JSX to flag a region as templatable:
 *
 *   <Slot name="brand-avatar"><img src={avatarUrl} /></Slot>
 *
 * Behavior:
 *  - When no host-page template is active for the surrounding `<Templated id="…">`,
 *    Slot renders its children inside two layers:
 *      (1) a `<span data-slot-name="X">` marker (CSS-selectable, layout-neutral
 *          via the `[data-slot-name] { display: contents }` rule in src/index.css);
 *      (2) a `<Templated id="lastbot-${name}">` that gives the embedder a
 *          dedicated per-slot override template — `<template id="lastbot-X">`
 *          replaces just this slot's content, regardless of whether any
 *          container template is also declared. Same shape used by
 *          `Templated.tsx#renderTemplate` at projection points.
 *
 *    The v2 drop-on-override check still applies — if this slot id is
 *    claimed by some *other* template, Slot renders nothing (the embedder
 *    relocated the content to their declared position; rendering it here too
 *    would duplicate).
 *  - When a host template IS active for the enclosing Templated, this Slot's
 *    children are harvested by Templated and projected into the matching
 *    `<slot name="…">` placeholder. The projection wraps the harvested
 *    content in the same `data-slot-name` marker AND per-slot Templated, so
 *    `<template id="lastbot-X">` works regardless of whether a container
 *    template is in play.
 */
export const Slot: FC<SlotProps> = ({ name, children, hidden }) => {
  const activeTemplateId = useTemplatedScope();
  const claimedElsewhere = useSlotClaimedElsewhere(name, activeTemplateId);
  // Hooks above run unconditionally so the hook order stays stable when
  // `hidden` toggles.
  if (hidden || claimedElsewhere) return null;
  // Skip the per-slot Templated wrap when a shell component for this slot
  // id exists (i.e. `lastbot-${name}` is in `FIXED_TEMPLATE_IDS`). The shell
  // already wraps itself in `<Templated id="lastbot-${name}">`, so wrapping
  // here would (a) duplicate the `data-slot-name` marker in the DOM and
  // (b) intercept the embedder template at the wrong layer — Templated's
  // harvest only walks JSX children, not into the shell component's
  // internal React tree, so any `<slot name="…">` placeholder inside the
  // embedder template would receive no projected content.
  //
  // Slot kind alone isn't enough to make this decision: `composer-text`
  // and `composer-voice` are `interactive` kind but have shells; using
  // the explicit shell-template list keeps the check accurate.
  return hasShellTemplate(name) ? (
    <>{children}</>
  ) : (
    <Templated id={`lastbot-${name}`}>{children}</Templated>
  );
};
